import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import useFetch from 'use-http';
import BackrsButton from '../../../../common/atoms/backrsButton/BackrsButton';
import StoryHorizontalScrollBar from './WholeMeStoryHorizontalScrollBar';
import WholeMeUploadManagerModal from './WholeMeUploadManagerModal';
import StoryUploadModal from './WholeMeStoryUploadModal';
import BaseModal from '../../../../common/atoms/BaseModal/BaseModal';
import appStrings from '../../../../common/app_strings';
import appColors from '../../../../common/app_colors';
import { handleError } from './utils';

const { buttons } = appColors;

const buttonStylesOverride = {
  width: '80px',
  height: '30px',
  mb: '20px',
  fontSize: '12px',
  backgroundColor: buttons.tetriaryBackground,
  textAlign: 'center',
  lineHeight: '1.75px',
  '&:hover': {
    backgroundColor: buttons.tetriaryBackground,
  },
  cursor: 'default',
};

const { wholeMeProfileStory: { storySection, deleting } } = appStrings;

const WholeMeScrollStorySection = ({
  profile,
  dataKey,
  headerLabel,
  editable,
  onStoryChange,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [type, setType] = useState(null);
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModelOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const questionId = headerLabel.toLowerCase();
  const { delete: del } = useFetch();
  const uploadMeta = {
    title: storySection.uploadMeta.title,
    description: storySection.uploadMeta.description(questionId),
    showUploadButton: true,
  };

  const openModal = (item) => {
    setOpen(true);
    setSelectedItem(item);
    setMode(item ? 'edit' : 'add');
    setType(item?.media_type || 'text');
  };

  const closeModal = () => {
    setOpen(false);
    setMode(null);
    setSelectedItem(null);
  };

  const openGenericModal = () => {
    setGenericModalOpen(true);
  };

  const closeGenericModal = () => {
    setGenericModalOpen(false);
  };

  const errorCallBack = (e) => {
    console.error('Error loading story video: ', e);
  };

  const openConfirmModal = () => {
    setConfirmModelOpen(true);
  };

  const handleConfirmModalClose = () => {
    setConfirmModelOpen(false);
    setItemToDelete(null);
  };

  const closeConfirmModal = () => {
    handleConfirmModalClose();
  };

  const handleItemDelete = async () => {
    try {
      const resp = await del(`/users-profile-answers/${itemToDelete.id}`);
      if (!resp) {
        throw new Error('handleonDelete: Failed to delete item');
      }
    } catch (error) {
      handleError(error);
    } finally {
      onStoryChange();
      handleConfirmModalClose();
      closeModal();
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    openConfirmModal();
  };

  const sectionData = profile[dataKey] || [];
  if (!sectionData && !editable) {
    return null;
  }

  const items = editable ? [uploadMeta, ...sectionData] : sectionData;

  return (
    <Box sx={{
      position: 'relative', width: '100%', minHeight: '300px', pt: '20px',
    }}
    >
      <BackrsButton disableRipple buttonStylesOverride={buttonStylesOverride} label={headerLabel} />
      <Stack direction="row" sx={{ overflowX: 'visible', overflowY: 'hidden' }}>
        <StoryHorizontalScrollBar
          items={items}
          editable={editable}
          openGenericModal={openGenericModal}
          onClickCallBack={openModal}
          errorCallBack={errorCallBack}
        />
      </Stack>
      <WholeMeUploadManagerModal
        isOpen={open}
        mode={mode}
        handleClose={closeModal}
        profile={profile}
        questionId={questionId}
        selectedItem={selectedItem}
        type={type}
        handleDeleteClick={handleDeleteClick}
        onStoryChange={onStoryChange}
      />
      <StoryUploadModal
        isOpen={genericModalOpen}
        handleClose={closeGenericModal}
        profile={profile}
        questionId={questionId}
        onStoryChange={onStoryChange}
      />
      <BaseModal isOpen={confirmModalOpen} handleClose={handleConfirmModalClose}>
        <Stack spacing={1.5} sx={{ padding: '10px' }}>
          <Typography>
            {deleting.areYouSure}
            {' '}
            {itemToDelete?.title}
            ?
          </Typography>
          <Stack
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: '25px',
            }}
          >
            <BackrsButton
              onClick={handleItemDelete}
              label={deleting.confirm}
              buttonStylesOverride={{
                maxWidth: '140px',
                maxHeight: '45px',
                fontWeight: '600',
                backgroundColor: buttons.darkNavy,
                '&:hover': { backgroundColor: buttons.darkNavy },
              }}
            />
            <BackrsButton
              onClick={closeConfirmModal}
              buttonStylesOverride={{
                maxWidth: '140px',
                maxHeight: '45px',
                fontWeight: '600',
                backgroundColor: buttons.warnigBackground,
                '&:hover': { backgroundColor: buttons.warnigBackground },
              }}
              label={deleting.cancel}
            />
          </Stack>
        </Stack>
      </BaseModal>
    </Box>
  );
};

export default WholeMeScrollStorySection;
